import { create } from 'zustand';
import type { ActivityEvent, Deployment, Subscription, Earning, AccountSummary } from '../types/account';

const DEPLOYMENTS_KEY = 'rivellum_deployments';

function loadDeployments(): Deployment[] {
  try {
    const raw = localStorage.getItem(DEPLOYMENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveDeployments(deployments: Deployment[]) {
  try { localStorage.setItem(DEPLOYMENTS_KEY, JSON.stringify(deployments)); } catch { /* ignore */ }
}

interface AccountState {
  /** Activity feed */
  activities: ActivityEvent[];
  /** All deployments */
  deployments: Deployment[];
  /** Subscriptions */
  subscriptions: Subscription[];
  /** Earnings records */
  earnings: Earning[];
  /** Computed account summary */
  summary: AccountSummary | null;

  addActivity: (event: ActivityEvent) => void;
  addDeployment: (d: Deployment) => void;
  updateDeploymentStatus: (id: string, status: Deployment['status']) => void;
  addSubscription: (sub: Subscription) => void;
  updateSubscription: (id: string, updates: Partial<Subscription>) => void;
  addEarning: (e: Earning) => void;
  setSummary: (s: AccountSummary) => void;
  clearAll: () => void;
}

export const useAccountStore = create<AccountState>()((set) => ({
  activities: [],
  deployments: loadDeployments(),
  subscriptions: [],
  earnings: [],
  summary: null,

  addActivity: (event) => set(s => ({
    activities: [event, ...s.activities].slice(0, 500),
  })),

  addDeployment: (d) => set(s => {
    const updated = [d, ...s.deployments];
    saveDeployments(updated);
    return { deployments: updated };
  }),

  updateDeploymentStatus: (id, status) => set(s => {
    const updated = s.deployments.map(d => d.id === id ? { ...d, status } : d);
    saveDeployments(updated);
    return { deployments: updated };
  }),

  addSubscription: (sub) => set(s => ({
    subscriptions: [...s.subscriptions, sub],
  })),

  updateSubscription: (id, updates) => set(s => ({
    subscriptions: s.subscriptions.map(sub => sub.id === id ? { ...sub, ...updates } : sub),
  })),

  addEarning: (e) => set(s => ({
    earnings: [e, ...s.earnings],
  })),

  setSummary: (summary) => set({ summary }),

  clearAll: () => set({
    activities: [],
    deployments: [],
    subscriptions: [],
    earnings: [],
    summary: null,
  }),
}));
